import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Loader2, TrendingUp, Megaphone, Search, Globe, Mail, Music2, ExternalLink, MousePointerClick, Bot, Sparkles } from "lucide-react";

const SOURCE_META = {
  meta_ads:       { label: "Meta Ads",        icon: Megaphone,         color: "#1877F2", chip: "rgba(24,119,242,0.15)"  },
  google_ads:     { label: "Google Ads",      icon: MousePointerClick, color: "#FBBC04", chip: "rgba(251,188,4,0.15)"   },
  chatgpt_ads:    { label: "ChatGPT Ads",     icon: Bot,               color: "#10A37F", chip: "rgba(16,163,127,0.15)"  },
  ai_organic:     { label: "AI Assistants (organic)", icon: Sparkles,  color: "#B794F6", chip: "rgba(183,148,246,0.15)" },
  google_organic: { label: "Organic Search",  icon: Search,            color: "#34A853", chip: "rgba(52,168,83,0.15)"   },
  email:          { label: "Email / Newsletter", icon: Mail,           color: "#FF8A5B", chip: "rgba(255,138,91,0.15)"  },
  tiktok_ads:     { label: "TikTok Ads",      icon: Music2,            color: "#FE2C55", chip: "rgba(254,44,85,0.15)"   },
  referral:       { label: "Referral Site",   icon: ExternalLink,      color: "#5EEAD4", chip: "rgba(94,234,212,0.15)"  },
  direct:         { label: "Direct / Unknown", icon: Globe,            color: "#9CA8B8", chip: "rgba(156,168,184,0.15)" },
};

const RANGES = [
  { value: 7,  label: "Last 7 days"  },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

function gbp(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(n || 0));
}

function ukDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
    }).format(new Date(iso));
  } catch (_) { return iso; }
}

export default function AdminAttribution() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/admin/attribution?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load attribution data"))
      .finally(() => setLoading(false));
  }, [days]);

  const sortedTotals = useMemo(() => {
    if (!data?.totals) return [];
    return Object.entries(data.totals)
      .map(([source, bucket]) => ({ source, ...bucket }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [data]);

  const filteredRecent = useMemo(() => {
    if (!data?.recent) return [];
    if (sourceFilter === "all") return data.recent;
    return data.recent.filter((o) => o.source === sourceFilter);
  }, [data, sourceFilter]);

  return (
    <div className="space-y-6" data-testid="admin-attribution-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="pg-micro mb-1">// Marketing Attribution</div>
          <h1 className="text-[#F7FAFC] text-3xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
            Where are sales coming from?
          </h1>
          <p className="text-[#9CA8B8] text-sm mt-1 max-w-2xl">
            Every paid order is bucketed using the strongest acquisition signal we captured —
            click ID first (Meta <code className="pg-mono text-xs text-[#A6FF00]">_fbc</code> /
            Google <code className="pg-mono text-xs text-[#A6FF00]">gclid</code> /
            ChatGPT <code className="pg-mono text-xs text-[#A6FF00]">oppref</code>), then UTM
            tags, then referrer. &quot;Direct&quot; means nothing was captured — typed URL, app open,
            or cookie-blocking browser.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#0B1220] border border-[#00C2D6]/20 rounded-sm p-1" data-testid="attribution-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`px-3 py-1.5 text-xs pg-mono uppercase tracking-[0.18em] rounded-sm transition-colors ${
                days === r.value
                  ? "bg-[#00C2D6]/15 text-[#00C2D6]"
                  : "text-[#9CA8B8] hover:text-[#F7FAFC]"
              }`}
              data-testid={`attribution-range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-[#9CA8B8] p-8" data-testid="attribution-loading">
          <Loader2 size={18} className="animate-spin text-[#5EEAD4]" />
          Loading attribution…
        </div>
      )}

      {error && !loading && (
        <div className="p-4 border border-[#FF8A5B]/40 bg-[#FF8A5B]/[0.06] text-[#FF8A5B] rounded-sm text-sm" data-testid="attribution-error">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Grand total + per-source cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="attribution-summary-cards">
            <KpiCard
              label="Total Revenue (paid)"
              value={gbp(data.grand_total.revenue)}
              sub={`${data.grand_total.orders} orders · last ${data.days}d`}
              accent="#A6FF00"
              icon={TrendingUp}
              testid="attribution-kpi-total-revenue"
            />
            {sortedTotals.slice(0, 3).map((b) => {
              const meta = SOURCE_META[b.source] || SOURCE_META.direct;
              return (
                <KpiCard
                  key={b.source}
                  label={meta.label}
                  value={gbp(b.revenue)}
                  sub={`${b.orders} orders · ${b.share_pct.toFixed(1)}%`}
                  accent={meta.color}
                  icon={meta.icon}
                  testid={`attribution-card-${b.source}`}
                />
              );
            })}
          </div>

          {/* Daily revenue by source */}
          <DailyRevenueChart timeseries={data.timeseries} />

          {/* Campaign breakdown */}
          {data.campaigns?.length > 0 && (
            <div className="pg-card p-5" data-testid="attribution-campaigns-card">
              <div className="pg-micro mb-3">// Revenue by campaign (utm_campaign)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] border-b border-[#00C2D6]/15">
                      <th className="py-2 pr-3">Campaign</th>
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3 text-right">Orders</th>
                      <th className="py-2 pr-3 text-right">Revenue</th>
                      <th className="py-2 pr-0 text-right">Avg Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c) => {
                      const meta = SOURCE_META[c.source] || SOURCE_META.direct;
                      return (
                        <tr key={c.campaign} className="border-b border-[#00C2D6]/8" data-testid={`attribution-campaign-${c.campaign}`}>
                          <td className="py-2 pr-3 text-[#F7FAFC] pg-mono text-xs">{c.campaign}</td>
                          <td className="py-2 pr-3">
                            <span className="pg-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: meta.color }}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right text-[#9CA8B8] pg-mono">{c.orders}</td>
                          <td className="py-2 pr-3 text-right text-[#F7FAFC] pg-mono">{gbp(c.revenue)}</td>
                          <td className="py-2 pr-0 text-right text-[#9CA8B8] pg-mono">{gbp(c.revenue / c.orders)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Full per-source table */}
          <div className="pg-card p-5">
            <div className="pg-micro mb-3">// Revenue by source</div>
            <div className="space-y-2" data-testid="attribution-source-list">
              {sortedTotals.map((b) => {
                const meta = SOURCE_META[b.source] || SOURCE_META.direct;
                const Icon = meta.icon;
                return (
                  <div
                    key={b.source}
                    className="flex items-center gap-3 p-3 border border-[#00C2D6]/15 rounded-sm hover:border-[#00C2D6]/40 transition-colors cursor-pointer"
                    onClick={() => setSourceFilter(sourceFilter === b.source ? "all" : b.source)}
                    data-testid={`attribution-row-${b.source}`}
                  >
                    <div
                      className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
                      style={{ backgroundColor: meta.chip }}
                    >
                      <Icon size={14} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#F7FAFC] text-sm">{meta.label}</div>
                      <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
                        {b.orders} orders
                        {b.sessions > 0 && (
                          <span> · {b.sessions} visits · {b.conv_pct != null ? `${b.conv_pct}% conv` : "—"}</span>
                        )}
                      </div>
                    </div>
                    {/* Share bar */}
                    <div className="w-32 lg:w-56 h-1.5 bg-[#0B1220] border border-[#00C2D6]/10 rounded-sm overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${b.share_pct}%`, backgroundColor: meta.color }}
                      />
                    </div>
                    <div className="text-right shrink-0 w-28">
                      <div className="text-[#F7FAFC] pg-mono">{gbp(b.revenue)}</div>
                      <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
                        {b.share_pct.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {sortedTotals.every((b) => b.orders === 0) && (
              <div className="text-center text-[#9CA8B8] py-8 text-sm">
                No paid orders in the last {data.days} days.
              </div>
            )}
          </div>

          {/* Recent paid orders annotated with bucket */}
          <div className="pg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div>
                <div className="pg-micro">// Recent paid orders</div>
                <div className="text-[#9CA8B8] text-xs mt-1">
                  Last 50 paid orders with their classified source. Click a source above to filter.
                </div>
              </div>
              {sourceFilter !== "all" && (
                <button
                  onClick={() => setSourceFilter("all")}
                  className="text-[#5EEAD4] hover:text-[#A6FF00] text-xs pg-mono uppercase tracking-[0.18em]"
                  data-testid="attribution-clear-filter"
                >
                  Clear filter ({SOURCE_META[sourceFilter]?.label || sourceFilter}) ✕
                </button>
              )}
            </div>
            <div className="overflow-x-auto" data-testid="attribution-recent-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] border-b border-[#00C2D6]/15">
                    <th className="py-2 pr-3">Placed</th>
                    <th className="py-2 pr-3">Order #</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3">Total</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Signal</th>
                    <th className="py-2 pr-3">Campaign / Referrer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecent.map((o) => {
                    const meta = SOURCE_META[o.source] || SOURCE_META.direct;
                    const Icon = meta.icon;
                    const signal =
                      o.fbclid ? "fbclid" :
                      o.gclid ? "gclid" :
                      o.oppref ? "oppref" :
                      o.utm_source ? `utm:${o.utm_source}` :
                      o.referrer ? "referrer" : "—";
                    const campaignDetail =
                      o.utm_campaign ||
                      (o.referrer ? new URL(o.referrer.startsWith("http") ? o.referrer : `https://${o.referrer}`).hostname : "—");
                    return (
                      <tr key={o.order_number} className="border-b border-[#00C2D6]/8 hover:bg-[#0B1220]/40">
                        <td className="py-2 pr-3 text-[#9CA8B8] pg-mono text-xs">{ukDate(o.created_at)}</td>
                        <td className="py-2 pr-3 text-[#F7FAFC] pg-mono text-xs">{o.order_number}</td>
                        <td className="py-2 pr-3 text-[#9CA8B8] text-xs truncate max-w-[200px]">{o.email || "—"}</td>
                        <td className="py-2 pr-3 text-[#F7FAFC] pg-mono">{gbp(o.total)}</td>
                        <td className="py-2 pr-3">
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm pg-mono text-[10px] uppercase tracking-[0.18em]"
                            style={{ backgroundColor: meta.chip, color: meta.color }}
                          >
                            <Icon size={10} />
                            {meta.label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-[#9CA8B8] pg-mono text-[10px] uppercase tracking-[0.18em]">{signal}</td>
                        <td className="py-2 pr-3 text-[#9CA8B8] text-xs truncate max-w-[260px]">
                          {campaignDetail}
                          {o.landing_path && o.landing_path !== "/" && (
                            <span className="block pg-mono text-[10px] text-[#5B6776] truncate">↳ {o.landing_path}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredRecent.length === 0 && (
                <div className="text-center text-[#9CA8B8] py-6 text-sm">
                  No paid orders match this filter.
                </div>
              )}
            </div>
          </div>

          {/* Tips */}
          <div className="pg-card p-4 text-xs text-[#9CA8B8] leading-relaxed">
            <span className="text-[#5EEAD4] pg-mono uppercase tracking-[0.18em] block mb-1">
              Tip · attribution accuracy
            </span>
            Orders placed before the attribution capture went live (
            <span className="text-[#F7FAFC]">25&nbsp;June&nbsp;2026</span>) won&apos;t have UTM /
            click ID data on file, so most will fall into <strong className="text-[#F7FAFC]">Direct</strong> or{" "}
            <strong className="text-[#F7FAFC]">Referral</strong>. Going forward, paid Meta / Google ads
            should automatically light up the right buckets — provided the landing URLs preserve the
            <code className="pg-mono text-[#A6FF00]"> ?fbclid=… </code> / <code className="pg-mono text-[#A6FF00]">?gclid=…</code> /
            <code className="pg-mono text-[#A6FF00]"> ?utm_*=…</code> parameters Meta and Google append.
          </div>
        </>
      )}
    </div>
  );
}

function DailyRevenueChart({ timeseries }) {
  const days = useMemo(() => {
    const m = {};
    for (const r of timeseries || []) {
      if (!m[r.date]) m[r.date] = { date: r.date, total: 0 };
      m[r.date][r.source] = (m[r.date][r.source] || 0) + r.revenue;
      m[r.date].total += r.revenue;
    }
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
  }, [timeseries]);

  if (days.length === 0) return null;
  const max = Math.max(1, ...days.map((d) => d.total));
  const sources = Object.keys(SOURCE_META);

  return (
    <div className="pg-card p-5" data-testid="attribution-daily-chart">
      <div className="pg-micro mb-4">// Daily revenue by source</div>
      <div className="flex items-end gap-1 h-36">
        {days.map((d) => (
          <div
            key={d.date}
            className="flex-1 flex flex-col justify-end gap-px min-w-[3px]"
            title={`${d.date}: ${gbp(d.total)}`}
          >
            {sources.map((s) =>
              d[s] ? (
                <div
                  key={s}
                  style={{ height: `${(d[s] / max) * 100}%`, backgroundColor: SOURCE_META[s].color, minHeight: 1 }}
                  title={`${d.date} · ${SOURCE_META[s].label}: ${gbp(d[s])}`}
                />
              ) : null
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-x-4 gap-y-1 mt-3 flex-wrap">
        {sources.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 pg-mono text-[10px] uppercase tracking-[0.14em] text-[#9CA8B8]">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: SOURCE_META[s].color }} />
            {SOURCE_META[s].label}
          </span>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div
      className="pg-card p-4 border-l-2"
      style={{ borderLeftColor: accent }}
      data-testid={testid || `attribution-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">{label}</div>
        {Icon && <Icon size={14} style={{ color: accent }} />}
      </div>
      <div className="text-[#F7FAFC] pg-display text-2xl" style={{ fontFamily: "'Exo 2'" }}>{value}</div>
      {sub && <div className="text-[#9CA8B8] text-xs mt-1">{sub}</div>}
    </div>
  );
}
